import { useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  ArrowLeft,
  Camera,
  Check,
  CheckCircle2,
  ChevronRight,
  RotateCcw,
  XCircle,
} from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import ProductVisual from "@/components/ProductVisual";
import StatusBadge from "@/components/StatusBadge";
import { ANSWERS, getInspectionResult, QUESTIONS, formatRupees } from "@/lib/returns";
import { useReturns } from "@/contexts/ReturnsContext";

function AnswerIcon({ tone }) {
  if (tone === "good") {
    return <CheckCircle2 size={25} aria-hidden="true" />;
  }
  if (tone === "minor") {
    return <AlertTriangle size={25} aria-hidden="true" />;
  }
  return <XCircle size={25} aria-hidden="true" />;
}

export default function InspectionPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { returns, updateInspection } = useReturns();
  const item = returns.find((returnItem) => returnItem.order_id === orderId);
  const [answers, setAnswers] = useState({});
  const [evidence, setEvidence] = useState({});
  const [step, setStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const fileInputRef = useRef(null);

  const question = QUESTIONS[step];
  const currentAnswer = answers[question?.id]?.value;
  const currentEvidence = evidence[question?.id];
  const evidenceRequired = currentAnswer === "minor" || currentAnswer === "major";
  const result = useMemo(() => getInspectionResult(answers), [answers]);

  if (!item) {
    return (
      <main className="inspection-shell empty-screen" data-testid="missing-inspection-page">
        <PackageSearchMessage />
      </main>
    );
  }

  const chooseAnswer = (value) => {
    setAnswers((current) => ({ ...current, [question.id]: { value } }));
    if (value === "good") {
      setEvidence((current) => {
        const nextEvidence = { ...current };
        delete nextEvidence[question.id];
        return nextEvidence;
      });
    }
  };

  const readEvidence = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setEvidence((current) => ({
        ...current,
        [question.id]: { dataUrl: String(reader.result), label: file.name },
      }));
    };
    reader.readAsDataURL(file);
  };

  const advance = () => {
    if (step < QUESTIONS.length - 1) {
      setStep((current) => current + 1);
    } else {
      setStep(QUESTIONS.length);
    }
  };

  const submitInspection = () => {
    updateInspection(orderId, {
      answers,
      evidence,
      ...result,
      submitted_at: new Date().toISOString(),
      review_status: result.disposition === "Restock" ? "Not required" : "Unreviewed",
      flag_note: "",
    });
    setSubmitted(true);
  };

  const inspectNext = () => {
    const next = returns.find((returnItem) => !returnItem.inspection && returnItem.order_id !== orderId);
    navigate(next ? `/inspect/${next.order_id}` : "/");
  };

  if (submitted) {
    return (
      <main className="inspection-shell" data-testid="inspection-success-page">
        <section className="submitted-card">
          <span className="success-icon"><Check size={28} aria-hidden="true" /></span>
          <p className="eyebrow">Inspection saved</p>
          <h1>Item is ready for its next step.</h1>
          <StatusBadge disposition={result.disposition} />
          <p>Grade: <strong>{result.grade}</strong></p>
          <button
            type="button"
            className="button button-primary button-large"
            onClick={inspectNext}
            data-testid="inspect-next-item-button"
          >
            Inspect next item <ChevronRight size={19} aria-hidden="true" />
          </button>
          <Link className="quiet-link" to="/" data-testid="back-to-returns-link">
            Back to returns
          </Link>
        </section>
      </main>
    );
  }

  if (step === QUESTIONS.length) {
    return (
      <main className="inspection-shell" data-testid="inspection-summary-page">
        <section className="inspection-topbar">
          <Link to="/" className="back-link" data-testid="inspection-back-link">
            <ArrowLeft size={18} aria-hidden="true" /> Returns
          </Link>
          <span className="step-counter">Final check</span>
        </section>
        <section className="result-card">
          <p className="eyebrow">Auto-calculated result</p>
          <h1>Review before submission</h1>
          <div className="result-grade-block">
            <span className="grade-code">{result.grade}</span>
            <StatusBadge disposition={result.disposition} />
          </div>
          <div className="result-product-row">
            <ProductVisual item={item} size="small" />
            <div><strong>{item.product_name}</strong><span>{item.variant}</span></div>
          </div>
          <div className="result-reasons">
            <span>Why this result</span>
            <ul>{result.reasons.map((reason) => <li key={reason}>{reason}</li>)}</ul>
          </div>
          <button
            type="button"
            className="button button-primary button-large"
            onClick={submitInspection}
            data-testid="confirm-submit-inspection-button"
          >
            Confirm and submit <Check size={19} aria-hidden="true" />
          </button>
          <button
            type="button"
            className="back-button"
            onClick={() => setStep(QUESTIONS.length - 1)}
            data-testid="edit-inspection-button"
          >
            <ArrowLeft size={17} aria-hidden="true" /> Edit checks
          </button>
        </section>
      </main>
    );
  }

  return (
    <main className="inspection-shell" data-testid="inspection-page">
      <section className="inspection-topbar">
        <Link to="/" className="back-link" data-testid="inspection-back-link">
          <ArrowLeft size={18} aria-hidden="true" /> Returns
        </Link>
        <span className="step-counter" data-testid="inspection-progress-label">
          {step + 1} of {QUESTIONS.length}
        </span>
      </section>
      <div className="inspection-progress" aria-hidden="true">
        <span style={{ width: `${((step + 1) / QUESTIONS.length) * 100}%` }} />
      </div>
      <section className="item-summary">
        <ProductVisual item={item} size="normal" />
        <div className="item-summary-copy">
          <span className="cell-code">{item.order_id}</span>
          <h1>{item.product_name}</h1>
          <p>{item.variant} · {formatRupees(item.mrp)}</p>
          <div className="return-reason"><span>Customer reason</span>{item.return_reason}</div>
        </div>
      </section>
      <section className="question-card" data-testid={`inspection-question-${question.id}`}>
        <p className="eyebrow">Check {step + 1}</p>
        <h2>{question.hindi}</h2>
        <p className="question-translation">{question.english}</p>
        <div className="answer-grid" role="group" aria-label={question.english}>
          {ANSWERS.map((answer) => (
            <button
              key={answer.value}
              type="button"
              className={`answer-card answer-${answer.tone} ${
                currentAnswer === answer.value ? "is-selected" : ""
              }`}
              onClick={() => chooseAnswer(answer.value)}
              data-testid={`answer-${question.id}-${answer.value}`}
            >
              <AnswerIcon tone={answer.tone} />
              <span><strong>{answer.hindi}</strong><small>{answer.english}</small></span>
            </button>
          ))}
        </div>
        {evidenceRequired && (
          <div className="evidence-box" data-testid="mandatory-photo-section">
            <div className="evidence-copy">
              <Camera size={20} aria-hidden="true" />
              <div><strong>Photo zaroori hai</strong><span>Photo evidence is required for an issue.</span></div>
            </div>
            {currentEvidence?.dataUrl ? (
              <div className="captured-evidence">
                <img src={currentEvidence.dataUrl} alt="Captured inspection evidence" />
                <button
                  type="button"
                  className="retake-button"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="retake-photo-button"
                >
                  <RotateCcw size={16} aria-hidden="true" /> Retake
                </button>
              </div>
            ) : (
              <button
                type="button"
                className="capture-button"
                onClick={() => fileInputRef.current?.click()}
                data-testid="capture-photo-button"
              >
                <Camera size={18} aria-hidden="true" /> Add photo evidence
              </button>
            )}
            <input
              ref={fileInputRef}
              className="sr-only"
              type="file"
              accept="image/*"
              capture="environment"
              onChange={readEvidence}
              data-testid="issue-photo-file-input"
            />
          </div>
        )}
        <button
          type="button"
          className="button button-primary button-large"
          disabled={!currentAnswer || (evidenceRequired && !currentEvidence?.dataUrl)}
          onClick={advance}
          data-testid="continue-inspection-button"
        >
          {step === QUESTIONS.length - 1 ? "See result" : "Continue"}
          <ChevronRight size={19} aria-hidden="true" />
        </button>
      </section>
    </main>
  );
}

function PackageSearchMessage() {
  return (
    <section className="submitted-card">
      <p className="eyebrow">Not found</p>
      <h1>This return is not available.</h1>
      <Link className="button button-primary" to="/" data-testid="missing-item-back-button">
        Back to returns
      </Link>
    </section>
  );
}